/**
 * tokenCombatState — resolver for per-token combat stats.
 *
 * The Roll20 linked-vs-unlinked bar model:
 *
 * - Linked token (character.isMob === false, or no character):
 *   Stats are read from the shared character sheet (`character.stats.hp`).
 *   Multiple tokens share one sheet. Editing one HP affects all of them.
 *
 * - Mob token (token.instanceStats !== null):
 *   Stats are read from `token.instanceStats`. The character sheet is a
 *   read-only template; each placed token tracks its own HP. The flag
 *   is `token.instanceStats !== null` (not `character.isMob`) so the
 *   resolver works without a character lookup.
 */

import type { Token } from '@/types'
import type { VTTCharacterData } from '@/services/characterService'

export interface CombatStats {
  hp: { current: number; max: number; temp: number }
  /** Source indicates whether stats came from the token's own instanceStats
   *  or the shared character sheet. Useful for callers deciding the write path. */
  source: 'instanceStats' | 'sheet'
}

/**
 * Return the authoritative combat stats for a token.
 *
 * - If `token.instanceStats` is non-null → mob path; use instanceStats.
 * - Otherwise → linked path; use `character.stats.hp`.
 *
 * Both branches return a typed `CombatStats` object with `source` set so
 * callers can branch on write path without re-checking the condition.
 */
export function getTokenCombatState(
  token: Pick<Token, 'instanceStats'>,
  character: VTTCharacterData | null | undefined,
): CombatStats {
  if (token.instanceStats != null) {
    return {
      hp: {
        current: token.instanceStats.hp.current,
        max: token.instanceStats.hp.max,
        temp: token.instanceStats.hp.temp,
      },
      source: 'instanceStats',
    }
  }

  const sheetHp = character?.stats?.hp ?? { current: 0, max: 0, temp: 0 }
  return {
    hp: {
      current: sheetHp.current,
      max: sheetHp.max,
      temp: sheetHp.temp,
    },
    source: 'sheet',
  }
}

/**
 * Convenience: is this token in mob mode (independent HP)?
 * True when instanceStats is non-null — doesn't require the character object.
 */
export function isMobToken(token: Pick<Token, 'instanceStats'>): boolean {
  return token.instanceStats != null
}
